import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import * as signalR from '@microsoft/signalr';
import { notificationsApi, createSignalRConnection } from '../services/api';
import { useAppAuth } from '../hooks/useAppAuth';
import { useRefresh } from '../hooks/useRefresh';
import { RefreshButton } from '../components/RefreshButton';
import type { NotificationDto, NotificationPreference } from '../types/modules';
import { NotificationChannel, NotificationEventType } from '../types/modules';

const C = {
  bg: '#060E18', surface: '#0D1B2A', border: 'rgba(255,255,255,0.07)',
  text: '#E8EDF2', muted: '#667788', green: '#2AFFA0', blue: '#64B5F6',
  amber: '#FFA726', red: '#FF5252', purple: '#CE93D8',
};

const eventLabels: Partial<Record<NotificationEventType, string>> = {
  [NotificationEventType.JobAlertMatch]:       'New Job Alert Match',
  [NotificationEventType.Outbid]:              'Outbid',
  [NotificationEventType.JobApproved]:         'Job Approved',
  [NotificationEventType.QuoteReceived]:       'New Quote',
  [NotificationEventType.QuoteAccepted]:       'Quote Accepted',
  [NotificationEventType.ContractGenerated]:   'Contract Generated',
  [NotificationEventType.PaymentReleased]:     'Payment Released',
  [NotificationEventType.ReferralEarning]:     'Referral Income',
  [NotificationEventType.DocumentExpiry30]:    'Document Expiry (30 days)',
  [NotificationEventType.DocumentExpiry15]:    'Document Expiry (15 days)',
  [NotificationEventType.DocumentExpiry7]:     'Document Expiry (7 days)',
  [NotificationEventType.DocumentExpiry1]:     'Document Expires Tomorrow',
  [NotificationEventType.InsuranceExpiry30]:   'Insurance Expiry (30 days)',
  [NotificationEventType.InsuranceExpiry15]:   'Insurance Expiry (15 days)',
  [NotificationEventType.InsuranceExpiry7]:    'Insurance Expiry (7 days)',
  [NotificationEventType.InsuranceExpiry1]:    'Insurance Expires Tomorrow',
  [NotificationEventType.VerificationComplete]: 'Verification Approved',
  [NotificationEventType.DisputeOpened]:       'Dispute Opened',
  [NotificationEventType.DisputeResolved]:     'Dispute Resolved',
  [NotificationEventType.DriverLoggedIn]:      'Driver Logged In',
};

const eventIcon: Partial<Record<NotificationEventType, string>> = {
  [NotificationEventType.JobAlertMatch]:       '🔔',
  [NotificationEventType.Outbid]:              '⚡',
  [NotificationEventType.JobApproved]:         '✅',
  [NotificationEventType.QuoteReceived]:       '💬',
  [NotificationEventType.QuoteAccepted]:       '🤝',
  [NotificationEventType.ContractGenerated]:   '📄',
  [NotificationEventType.PaymentReleased]:     '💰',
  [NotificationEventType.ReferralEarning]:     '🎁',
  [NotificationEventType.DocumentExpiry30]:    '📅',
  [NotificationEventType.DocumentExpiry15]:    '📅',
  [NotificationEventType.DocumentExpiry7]:     '⚠️',
  [NotificationEventType.DocumentExpiry1]:     '🚨',
  [NotificationEventType.InsuranceExpiry30]:   '📅',
  [NotificationEventType.InsuranceExpiry15]:   '📅',
  [NotificationEventType.InsuranceExpiry7]:    '⚠️',
  [NotificationEventType.InsuranceExpiry1]:    '🚨',
  [NotificationEventType.VerificationComplete]: '🛡️',
  [NotificationEventType.DisputeOpened]:       '🔴',
  [NotificationEventType.DisputeResolved]:     '✅',
  [NotificationEventType.DriverLoggedIn]:      '🚗',
};

const eventAccent: Partial<Record<NotificationEventType, string>> = {
  [NotificationEventType.PaymentReleased]:     C.green,
  [NotificationEventType.ReferralEarning]:     C.purple,
  [NotificationEventType.JobAlertMatch]:       C.blue,
  [NotificationEventType.Outbid]:              C.amber,
  [NotificationEventType.DocumentExpiry7]:     C.amber,
  [NotificationEventType.DocumentExpiry15]:    C.amber,
  [NotificationEventType.DocumentExpiry30]:    C.amber,
  [NotificationEventType.DocumentExpiry1]:     C.red,
  [NotificationEventType.InsuranceExpiry7]:    C.amber,
  [NotificationEventType.InsuranceExpiry15]:   C.amber,
  [NotificationEventType.InsuranceExpiry30]:   C.amber,
  [NotificationEventType.InsuranceExpiry1]:    C.red,
  [NotificationEventType.VerificationComplete]: C.green,
  [NotificationEventType.DisputeOpened]:       C.red,
  [NotificationEventType.DisputeResolved]:     C.green,
  [NotificationEventType.DriverLoggedIn]:      C.blue,
};

const SIGNALR_EVENTS = [
  'JobAlertMatch', 'Outbid', 'JobApproved', 'QuoteReceived', 'QuoteAccepted',
  'ContractGenerated', 'PaymentReleased', 'ReferralEarning',
  'DocumentExpiryWarning', 'VerificationComplete', 'InsuranceExpiry',
  'DisputeOpened', 'DisputeResolved', 'DriverLoggedIn',
];

function ToggleSwitch({ value, onChange, label }: { value: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
      <span style={{ fontSize: 13, color: C.text }}>{label}</span>
      <button onClick={() => onChange(!value)} style={{
        width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
        background: value ? C.green : 'rgba(255,255,255,0.15)',
        transition: 'background 0.2s', position: 'relative',
      }}>
        <div style={{
          position: 'absolute', top: 3, left: value ? 23 : 3,
          width: 18, height: 18, borderRadius: '50%',
          background: value ? C.bg : '#888',
          transition: 'left 0.2s',
        }} />
      </button>
    </div>
  );
}

export default function NotificationsPage() {
  const { account, getToken } = useAppAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [prefs, setPrefs]                 = useState<NotificationPreference | null>(null);
  const [unread, setUnread]               = useState(0);
  const [tab, setTab]                     = useState<'inbox' | 'preferences'>('inbox');
  const [loading, setLoading]             = useState(true);
  const [prefDirty, setPrefDirty]         = useState(false);
  const [saving, setSaving]               = useState(false);

  const { isRefreshing, lastRefreshed, refresh } = useRefresh(
    useCallback(async () => {
      if (!account) return;
      try {
        const token = await getToken();
        const [n, u, p] = await Promise.all([
          notificationsApi.getMine(token),
          notificationsApi.getUnreadCount(token),
          notificationsApi.getPrefs(token),
        ]);
        setNotifications(n);
        setUnread(u.count);
        setPrefs(p);
      } catch { /* non-fatal */ }
    }, [account, getToken])
  );

  const loadData = useCallback(async () => {
    if (!account) return;
    try {
      const token = await getToken();
      const [n, u, p] = await Promise.all([
        notificationsApi.getMine(token),
        notificationsApi.getUnreadCount(token),
        notificationsApi.getPrefs(token),
      ]);
      setNotifications(n);
      setUnread(u.count);
      setPrefs(p);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [account, getToken]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── SignalR — listen for new notifications ────────────────────────────
  useEffect(() => {
    if (!account) return;
    let conn: signalR.HubConnection | null = null;

    (async () => {
      conn = createSignalRConnection('/hubs/jobs', getToken);
      await conn.start().catch(console.error);

      const refresh = () => {
        loadData();
        qc.invalidateQueries({ queryKey: ['notifications', 'unread'] });
      };
      SIGNALR_EVENTS.forEach(evt => conn!.on(evt, refresh));
    })();

    return () => { conn?.stop().catch(console.error); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const markRead = async (n: NotificationDto) => {
    if (!n.isRead) {
      const token = await getToken();
      await notificationsApi.markRead(n.id, token).catch(() => {});
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, isRead: true } : x));
      setUnread(c => Math.max(0, c - 1));
      qc.invalidateQueries({ queryKey: ['notifications', 'unread'] });
    }
    if (n.eventType === NotificationEventType.DriverLoggedIn) navigate('/fleet-map');
    else if (n.jobId) navigate(`/jobs/${n.jobId}`);
  };

  const markAllRead = async () => {
    const token = await getToken();
    await notificationsApi.markAllRead(token).catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnread(0);
    qc.invalidateQueries({ queryKey: ['notifications', 'unread'] });
  };

  const savePrefs = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const token = await getToken();
      await notificationsApi.savePrefs(prefs, token);
      setPrefDirty(false);
    } catch { /* no-op */ }
    finally { setSaving(false); }
  };

  const updatePref = (key: keyof NotificationPreference, value: boolean) => {
    setPrefs(p => p ? { ...p, [key]: value } : p);
    setPrefDirty(true);
  };

  const channelLabel: Record<NotificationChannel, string> = {
    [NotificationChannel.Email]: 'Email',
    [NotificationChannel.Sms]:   'SMS',
    [NotificationChannel.Push]:  'Push',
    [NotificationChannel.InApp]: 'In-App',
  };
  const channelColour: Record<NotificationChannel, string> = {
    [NotificationChannel.Email]: C.blue,
    [NotificationChannel.Sms]:   C.amber,
    [NotificationChannel.Push]:  C.purple,
    [NotificationChannel.InApp]: C.green,
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}>Loading…</div>
  );

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>

      {/* Header */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: 'linear-gradient(135deg,#2AFFA0,#00C9FF)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 11, color: C.bg }}>TD</div>
          <span style={{ fontWeight: 700 }}>Notifications</span>
          {unread > 0 && (
            <span style={{
              background: C.red, color: '#fff', borderRadius: 10, padding: '1px 7px',
              fontSize: 11, fontWeight: 700, minWidth: 18, textAlign: 'center'
            }}>{unread}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RefreshButton onRefresh={refresh} isRefreshing={isRefreshing} lastRefreshed={lastRefreshed}
            className="text-gray-400 hover:text-gray-200 hover:bg-white/10" />
          {tab === 'inbox' && unread > 0 && (
            <button onClick={markAllRead} style={{
              background: 'transparent', border: `1px solid ${C.border}`,
              color: C.muted, padding: '6px 12px', borderRadius: 6, fontSize: 12,
              cursor: 'pointer', fontFamily: 'inherit'
            }}>Mark all read</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: '0 24px', display: 'flex' }}>
        {(['inbox', 'preferences'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '11px 18px', border: 'none', background: 'transparent',
            color: tab === t ? C.green : C.muted, fontWeight: tab === t ? 700 : 400,
            borderBottom: tab === t ? `2px solid ${C.green}` : '2px solid transparent',
            fontSize: 13, cursor: 'pointer', textTransform: 'capitalize', fontFamily: 'inherit',
          }}>{t}</button>
        ))}
      </div>

      <div style={{ width: '100%', padding: '24px' }}>

        {/* ── Inbox ────────────────────────────────────────────────────── */}
        {tab === 'inbox' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {notifications.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: C.muted }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🔔</div>
                <div>You're all caught up! No notifications yet.</div>
              </div>
            ) : notifications.map(n => {
              const accent = eventAccent[n.eventType] ?? C.blue;
              const icon   = eventIcon[n.eventType] ?? '●';
              return (
                <div key={n.id} onClick={() => markRead(n)} style={{
                  background: n.isRead ? C.surface : 'rgba(42,255,160,0.04)',
                  border: `1px solid ${n.isRead ? C.border : 'rgba(42,255,160,0.15)'}`,
                  borderLeft: `3px solid ${n.isRead ? 'transparent' : accent}`,
                  borderRadius: 10, padding: '14px 16px', cursor: (n.eventType === NotificationEventType.DriverLoggedIn || n.jobId) ? 'pointer' : n.isRead ? 'default' : 'pointer',
                  transition: 'all 0.2s', display: 'flex', gap: 14, alignItems: 'flex-start'
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: `${accent}18`, border: `1px solid ${accent}33`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16
                  }}>{icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ fontWeight: n.isRead ? 400 : 700, fontSize: 14, color: C.text }}>
                        {n.subject || eventLabels[n.eventType] || 'Notification'}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <span style={{
                          fontSize: 10, color: channelColour[n.channel],
                          background: `${channelColour[n.channel]}18`,
                          border: `1px solid ${channelColour[n.channel]}33`,
                          padding: '1px 6px', borderRadius: 4
                        }}>{channelLabel[n.channel]}</span>
                        {!n.isRead && (
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: accent, marginTop: 3 }} />
                        )}
                      </div>
                    </div>
                    <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0', lineHeight: 1.5 }}>
                      {n.body.length > 120 ? n.body.slice(0, 117) + '…' : n.body}
                    </p>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 5 }}>
                      {new Date(n.createdAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Preferences ──────────────────────────────────────────────── */}
        {tab === 'preferences' && prefs && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 22px' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Notification Channels</h3>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
                Choose how you receive notifications. Urgent alerts are always sent via all active channels.
              </p>
              <div style={{ borderTop: `1px solid ${C.border}` }}>
                <ToggleSwitch value={prefs.emailEnabled} onChange={v => updatePref('emailEnabled', v)} label="📧  Email notifications" />
                <div style={{ height: 1, background: C.border }} />
                <ToggleSwitch value={prefs.smsEnabled} onChange={v => updatePref('smsEnabled', v)} label="📱  SMS (high-priority & urgent only)" />
                <div style={{ height: 1, background: C.border }} />
                <ToggleSwitch value={prefs.pushEnabled} onChange={v => updatePref('pushEnabled', v)} label="🔔  Push notifications" />
                <div style={{ height: 1, background: C.border }} />
                <ToggleSwitch value={prefs.marketingEmail} onChange={v => updatePref('marketingEmail', v)} label="📰  Marketing & tips emails" />
              </div>
            </div>

            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 22px' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Important Alerts</h3>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
                These alerts cannot be disabled as they are required for platform compliance.
              </p>
              {[
                { icon: '💰', label: 'Payment released (24–48hrs after POD)',        note: 'Always sent' },
                { icon: '⚠️', label: 'Document expiry (30d / 15d / 7d / daily)',      note: 'Always sent' },
                { icon: '🛡️', label: 'Insurance expiry (30d / 15d / 7d / daily)',     note: 'Always sent' },
                { icon: '📄', label: 'Auto-contract generated on job acceptance',     note: 'Always sent' },
                { icon: '🔔', label: 'New job matching a saved alert',               note: 'Always sent' },
                { icon: '⚡', label: 'Outbid on a job',                              note: 'Always sent' },
                { icon: '🛡️', label: 'DVLA / insurance verification approved',       note: 'Always sent' },
                { icon: '🎁', label: 'Referral income credited',                     note: 'Always sent' },
              ].map(a => (
                <div key={a.label} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                  borderBottom: `1px solid ${C.border}`
                }}>
                  <span>{a.icon}</span>
                  <span style={{ flex: 1, fontSize: 13 }}>{a.label}</span>
                  <span style={{
                    fontSize: 11, color: C.green, background: 'rgba(42,255,160,0.1)',
                    border: '1px solid rgba(42,255,160,0.25)', padding: '2px 7px', borderRadius: 4
                  }}>{a.note}</span>
                </div>
              ))}
            </div>

            <button onClick={savePrefs} disabled={!prefDirty || saving} style={{
              padding: 14,
              background: prefDirty && !saving ? 'linear-gradient(135deg,#2AFFA0,#00C9FF)' : 'rgba(255,255,255,0.08)',
              border: 'none', borderRadius: 10, color: C.bg, fontWeight: 700, fontSize: 14,
              cursor: prefDirty && !saving ? 'pointer' : 'not-allowed', fontFamily: 'inherit'
            }}>
              {saving ? 'Saving…' : 'Save Preferences'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
